import { useState } from 'react';
import { Alert, Badge, Button, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useContentQuery, type ContentEventType } from '../../hooks/useContent';

// Session-scoped, deliberately NOT persisted to localStorage: a reason that
// survives a browser restart is a reason nobody is really giving.
let lastReason = '';

/** Test seam only. */
export function __resetLastReasonForTests() {
  lastReason = '';
}

interface Props {
  opened: boolean;
  onClose: () => void;
  userId: string;
  eventType: ContentEventType;
  resourceId: string;
  // `| undefined` for exactOptionalPropertyTypes: Timeline passes a non-chat row's
  // null conversation_id through as undefined. Same reason as useContent's Options.
  conversationId?: string | undefined;
}

export function ContentModal({
  opened,
  onClose,
  userId,
  eventType,
  resourceId,
  conversationId,
}: Props) {
  const [reason, setReason] = useState(lastReason);
  const [committed, setCommitted] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const query = useContentQuery({
    userId,
    eventType,
    resourceId,
    conversationId,
    reason: committed ?? '',
    enabled: committed !== null,
  });

  function submit() {
    const trimmed = reason.trim();
    if (!trimmed) {
      // Client-side check is a courtesy, not the control — the server rejects an
      // empty reason too, and writes no audit row when it does.
      setLocalError('A reason is required to read this content.');
      return;
    }
    setLocalError(null);
    lastReason = trimmed;
    setCommitted(trimmed);
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Read ${eventType.replace(/_/g, ' ')} content`}
      size="lg"
    >
      <Stack>
        {committed === null ? (
          <>
            <Text size="sm" c="dimmed">
              This read is recorded in the audit log — actor, subject, time, and this reason — and
              is visible to every other admin.
            </Text>
            <Textarea
              label="Reason"
              placeholder="e.g. ASC-601 support ticket"
              // NOT `autosize`: Mantine's Autosize.tsx reaches for a window
              // reference jsdom does not provide and throws on mount, taking every
              // test of this modal with it. Fixed rows costs nothing here — the
              // reason is one line, and the control is the reason, not its textarea.
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              error={localError}
              data-autofocus
            />
            <Button onClick={submit}>Show content</Button>
          </>
        ) : (
          <>
            {query.isFetching && <Text size="sm">Loading…</Text>}
            {query.isError && (
              <Alert color="red" title="Couldn't read this content">
                {query.error.message}
              </Alert>
            )}
            {query.data?.blocks.map((block) => (
              <Stack key={block.id} gap={4}>
                <Text size="xs" c="dimmed">
                  {block.heading}
                  {block.deleted && (
                    <Badge ml="xs" size="xs" color="red" variant="light">
                      deleted by user
                    </Badge>
                  )}
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {block.body}
                </Text>
              </Stack>
            ))}
            {query.data?.blocks.length === 0 && (
              <Text size="sm" c="dimmed">
                No content found for this event.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
